import {
  CreditCard,
  Globe,
  KeyRound,
  LifeBuoy,
  LogOut,
  Moon,
  Shield,
  Sun,
  User as UserIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/domains/identity/AuthProvider';
import { setStoredLocale, type Locale } from '@/shared/i18n';
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from '@/shared/ui';
import { routes } from '../routes';
import { useTheme } from './ThemeProvider';

export function AccountMenu() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { resolved, setChoice } = useTheme();

  if (!user) return null;

  const initials = (user.display_name || user.email)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  const otherLocale: Locale = i18n.language === 'vi' ? 'en' : 'vi';

  return (
    <Menu>
      <MenuTrigger
        aria-label={t('a11y.userMenu')}
        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-surface-raised text-xs font-semibold text-fg-muted transition-colors hover:bg-surface-hover hover:text-fg"
      >
        {initials || <UserIcon className="h-4 w-4" />}
      </MenuTrigger>

      <MenuContent className="w-60">
        <MenuLabel>
          <span className="block truncate font-medium text-fg">{user.display_name || user.email}</span>
          <span className="block truncate text-fg-subtle">{user.email}</span>
        </MenuLabel>
        <MenuSeparator />

        <MenuItem icon={<UserIcon className="h-4 w-4" />} onSelect={() => navigate(routes.accountProfile)}>
          {t('account.profile')}
        </MenuItem>
        <MenuItem icon={<Shield className="h-4 w-4" />} onSelect={() => navigate(routes.accountSecurity)}>
          {t('account.security')}
        </MenuItem>
        <MenuItem icon={<KeyRound className="h-4 w-4" />} onSelect={() => navigate(routes.accountApi)}>
          {t('account.api')}
        </MenuItem>
        <MenuItem icon={<CreditCard className="h-4 w-4" />} onSelect={() => navigate(routes.accountCredits)}>
          {t('account.credits')}
        </MenuItem>
        <MenuItem icon={<LifeBuoy className="h-4 w-4" />} onSelect={() => navigate(routes.support)}>
          {t('nav.support')}
        </MenuItem>

        <MenuSeparator />

        <MenuItem
          icon={resolved === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          onSelect={(event) => {
            event.preventDefault();
            setChoice(resolved === 'dark' ? 'light' : 'dark');
          }}
        >
          {resolved === 'dark' ? t('account.themeLight') : t('account.themeDark')}
        </MenuItem>
        <MenuItem
          icon={<Globe className="h-4 w-4" />}
          onSelect={(event) => {
            event.preventDefault();
            setStoredLocale(otherLocale);
          }}
        >
          {otherLocale === 'vi' ? 'Tiếng Việt' : 'English'}
        </MenuItem>

        <MenuSeparator />

        <MenuItem
          destructive
          icon={<LogOut className="h-4 w-4" />}
          onSelect={() => {
            logout();
            navigate(routes.login);
          }}
        >
          {t('auth.signOut')}
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}
